/**
 * Notes workspace layout (Apunte Digital): page tree on the
 * left, active page (Outlet) on the right.
 */
import { createRoute, Outlet, useLocation, useSearch } from "@tanstack/solid-router";
import { useNavigate } from "@tanstack/solid-router";
import { createEffect, createMemo, Show, untrack } from "solid-js";
import { FileText } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import type { Page } from "@apunte-digital/powersync";
import { appRoute } from "./route";
import { NotesSidebar } from "@/features/notes/components/notes-sidebar";
import { usePageMutations } from "@/features/notes/hooks/use-page-mutations";
import { flattenPageTree, usePageTree } from "@/features/notes/hooks/use-page-tree";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { SelectPageIllustration } from "@/components/ui/illustrations";
import { useI18n } from "@/providers/i18n-provider";

function NotesLayout() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const mutations = usePageMutations();
  const { tree } = usePageTree();

  // Addressable delete confirmation (`/notes?confirm=delete&page=<id>`):
  // the URL is the source of truth for WHICH dialog is open, so a copied
  // link REOPENS the exact dialog. SECURITY: deep-linking never executes the
  // delete — it only reopens the confirmation; the user must click "Delete". The
  // page is resolved by id against the loaded tree (if absent, it does not open).
  const urlSearch = useSearch({ from: notesRoute.id });
  const flatPages = createMemo(() => flattenPageTree(tree()));

  // URL → dialog: reflects `?confirm=delete&page=<id>` in the mutations signal.
  // CAUTION (CC-16, defensive): reading the state (`pageToDelete`) uses
  // `untrack` — the overlay is 100% URL-driven, so the effect should only
  // react to search-param changes; tracking the signal it writes
  // could revert a state-first close (pattern #15b).
  createEffect(() => {
    const wantsDelete = urlSearch().confirm === "delete";
    const id = urlSearch().page;
    const current = untrack(mutations.pageToDelete);
    if (wantsDelete && id) {
      if (current?.id !== id) {
        const page = untrack(flatPages).find((entry) => entry.id === id);
        if (page) {
          mutations.setPageToDelete(page);
        }
      }
    } else if (current) {
      mutations.setPageToDelete(null);
    }
  });

  // Preserves the current pathname (which may be `/notes/$pageId`): it only toggles
  // the search params inherited from the `/notes` layout, without losing the open note.
  const openDeleteConfirm = (page: Page) => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, confirm: "delete", page: page.id }),
      replace: true,
    });
  };
  const closeDeleteConfirm = () => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, confirm: undefined, page: undefined }),
      replace: true,
    });
  };

  const activePageId = () => {
    const match = /^\/notes\/([^/]+)/.exec(location().pathname);
    return match?.[1];
  };

  return (
    <div class="flex h-full min-h-0 flex-1">
      <NotesSidebar
        activePageId={activePageId}
        ready={mutations.ready}
        onCreatePage={(opts) => {
          void mutations.handleCreatePage(opts);
        }}
        onDeletePage={(page: Page) => {
          openDeleteConfirm(page);
        }}
        onRenamePage={(id, title) => {
          void mutations.handleRenamePage(id, title);
        }}
        onMovePage={(pageId, target) => {
          void mutations.handleMovePage(pageId, target);
        }}
      />
      <Outlet />
      <Show when={mutations.pageToDelete()}>
        {(page) => (
          <ConfirmDialog
            open
            onOpenChange={(open) => {
              if (!open) {
                closeDeleteConfirm();
              }
            }}
            title={t(msg`Move to trash?`)}
            description={t(
              msg`The page and all of its subpages move to the trash. You can restore them later from your profile.`,
            )}
            confirmLabel={t(msg`Move to trash`)}
            cancelLabel={t(msg`Cancel`)}
            variant="destructive"
            onConfirm={() => {
              void mutations.executeMoveToTrash(page());
            }}
            onCancel={() => {
              closeDeleteConfirm();
            }}
          />
        )}
      </Show>
    </div>
  );
}

function NotesEmptyState() {
  const { t } = useI18n();
  return (
    <div class="flex flex-1 items-center justify-center">
      <EmptyState
        icon={FileText}
        illustration={<SelectPageIllustration />}
        title={t(msg`Select a page`)}
        description={t(msg`Choose a page from the sidebar or create a new one.`)}
      />
    </div>
  );
}

interface NotesSearch {
  confirm?: "delete";
  page?: string;
}

export const notesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/notes",
  // Addressable delete confirmation (reopen, NEVER execute). It lives in the
  // layout because the dialog is mounted here and is valid for any note in the
  // tree (not only the one active in the path).
  validateSearch: (search: Record<string, unknown>): NotesSearch => ({
    confirm: search.confirm === "delete" ? "delete" : undefined,
    page: typeof search.page === "string" && search.page !== "" ? search.page : undefined,
  }),
  component: NotesLayout,
});

export const notesIndexRoute = createRoute({
  getParentRoute: () => notesRoute,
  path: "/",
  component: NotesEmptyState,
});
